// VideoEditorPanel — local, ephemeral video splicer.
//
// Drop multiple videos, reorder them, and splice into a single MP4 entirely
// in the browser via ffmpeg.wasm. Nothing is uploaded or persisted; the
// rendered file lives only as an in-memory object URL until you download or
// reset. Zero props (owns all state via useVideoEditor).

import React from 'react';
import {
  Download,
  Film,
  Loader2,
  RotateCcw,
  ShieldCheck,
  Sparkles,
  Trash2,
} from 'lucide-react';
import { useVideoEditor } from './useVideoEditor';
import DropZone from './DropZone';
import ClipList from './ClipList';
import { formatBytes, formatDuration } from './format';

export default function VideoEditorPanel() {
  const ed = useVideoEditor();
  const {
    clips,
    engine,
    engineError,
    preset,
    presets,
    progress,
    result,
    exporting,
    totalSize,
    totalDuration,
    addFiles,
    removeClip,
    moveClip,
    reorderClip,
    setPreset,
    exportVideo,
    clearResult,
    reset,
  } = ed;

  const hasClips = clips.length > 0;
  const pct = Math.round(progress.ratio * 100);
  const busy = exporting || engine === 'loading';

  return (
    <div className="h-full min-h-0 overflow-y-auto">
      <div className="mx-auto max-w-3xl flex flex-col gap-4 p-1">
        {/* Header */}
        <header className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <Film className="h-5 w-5 text-neon_purple-300" />
            <div>
              <h2 className="text-sm font-bold text-white/90">Video Editor</h2>
              <p className="text-[11px] text-neutral-500">
                Splice local clips into one MP4 — 100% in-browser, nothing uploaded.
              </p>
            </div>
          </div>
          {hasClips && (
            <button
              type="button"
              onClick={reset}
              disabled={busy}
              className="pac-glass-btn text-[11px] inline-flex items-center gap-1 shrink-0 disabled:opacity-40"
            >
              <Trash2 className="h-3.5 w-3.5" /> Clear all
            </button>
          )}
        </header>

        {/* Privacy badge */}
        <div className="flex items-center gap-2 rounded-lg border border-emerald-500/20 bg-emerald-500/[0.04] px-3 py-1.5">
          <ShieldCheck className="h-3.5 w-3.5 text-emerald-300 shrink-0" />
          <p className="text-[11px] text-emerald-200/90">
            Ephemeral session — clips and the rendered output never leave this tab and are
            discarded when you reset or navigate away.
          </p>
        </div>

        {/* Intake */}
        {!hasClips ? (
          <DropZone onFiles={addFiles} disabled={busy} />
        ) : (
          <DropZone onFiles={addFiles} disabled={busy} compact />
        )}

        {/* Clip timeline */}
        {hasClips && (
          <section className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-semibold text-neutral-300">
                Timeline · {clips.length} clip{clips.length === 1 ? '' : 's'}
              </h3>
              <p className="text-[11px] text-neutral-500 tabular-nums">
                {formatDuration(totalDuration)} · {formatBytes(totalSize)}
              </p>
            </div>
            <ClipList
              clips={clips}
              disabled={busy}
              onRemove={removeClip}
              onMove={moveClip}
              onReorder={reorderClip}
            />
            <p className="text-[11px] text-neutral-600">
              Drag clips to reorder. They are joined top-to-bottom.
            </p>
          </section>
        )}

        {/* Output settings + export */}
        {hasClips && (
          <section className="flex flex-col gap-3 rounded-xl pac-glass border border-neutral-700/40 p-3">
            <div className="flex flex-wrap items-center gap-3">
              <label className="flex items-center gap-2 text-[11px] text-neutral-400">
                Output
                <select
                  value={preset.id}
                  disabled={busy}
                  onChange={(e) =>
                    setPreset(presets.find((p) => p.id === e.target.value) ?? presets[0])
                  }
                  className="appearance-none cursor-pointer rounded-md border border-white/15 bg-black/60 px-2 py-1 text-[11px] font-medium text-white/90 hover:border-white/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-neon_purple-400/40 disabled:opacity-40"
                >
                  {presets.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </label>

              <button
                type="button"
                onClick={exportVideo}
                disabled={busy}
                className="ml-auto inline-flex items-center gap-2 rounded-lg bg-neon_purple-500/90 hover:bg-neon_purple-500 px-4 py-2 text-xs font-semibold text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {busy ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4" />
                )}
                {engine === 'loading'
                  ? 'Loading engine…'
                  : exporting
                    ? 'Splicing…'
                    : 'Splice & render'}
              </button>
            </div>

            {/* Progress */}
            {busy && (
              <div className="space-y-1.5">
                <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/10">
                  <div
                    className="h-full rounded-full bg-neon_purple-400 transition-[width] duration-200"
                    style={{ width: `${progress.phase === 'encoding' && pct > 0 ? pct : 8}%` }}
                  />
                </div>
                <div className="flex items-center justify-between text-[11px] text-neutral-400">
                  <span>{progress.label || 'Working…'}</span>
                  {progress.phase === 'encoding' && pct > 0 && (
                    <span className="tabular-nums">{pct}%</span>
                  )}
                </div>
                <p className="text-[10px] text-neutral-600">
                  First render downloads the ffmpeg engine (~30&nbsp;MB) once, then it is cached.
                </p>
              </div>
            )}

            {/* Error */}
            {progress.phase === 'error' && (
              <div className="rounded-lg border border-rose-500/30 bg-rose-500/[0.06] px-3 py-2">
                <p className="text-[11px] font-medium text-rose-200">Render failed</p>
                <p className="mt-0.5 break-words text-[11px] text-rose-300/80">
                  {engineError || progress.label}
                </p>
              </div>
            )}
          </section>
        )}

        {/* Result */}
        {result && (
          <section className="flex flex-col gap-3 rounded-xl pac-glass-raised border border-emerald-500/30 p-3">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-semibold text-emerald-200">Spliced video ready</h3>
              <button
                type="button"
                onClick={clearResult}
                aria-label="Discard render"
                className="rounded p-1 text-neutral-400 hover:text-white hover:bg-white/10"
              >
                <RotateCcw className="h-3.5 w-3.5" />
              </button>
            </div>
            <video
              src={result.url}
              controls
              playsInline
              className="w-full rounded-lg bg-black max-h-[50vh]"
            />
            <div className="flex items-center justify-between gap-3">
              <p className="text-[11px] text-neutral-500 tabular-nums">
                {formatDuration(result.durationSec)} · {formatBytes(result.size)}
              </p>
              <a
                href={result.url}
                download={result.filename}
                className="inline-flex items-center gap-2 rounded-lg bg-emerald-500/90 hover:bg-emerald-500 px-4 py-2 text-xs font-semibold text-white transition-colors"
              >
                <Download className="h-4 w-4" /> Download MP4
              </a>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
